
import React, { useState, useEffect } from 'react';
import { MapPin, Clock, Copy, ChevronDown, ChevronUp } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { useGoogleGeocoding } from '@/hooks/geocoding/useGoogleGeocoding';
import { format } from 'date-fns';
import { ru } from 'date-fns/locale';
import { toast } from '@/components/ui/sonner';

interface GpsPoint {
  id: string;
  latitude: number;
  longitude: number;
  created_at: string;
  rental_mileage: number;
  speed?: number;
}

interface GpsPointWithAddressProps {
  point: GpsPoint;
  cachedAddress?: string;
  onAddressLoaded?: (address: string) => void;
}

export const GpsPointWithAddress: React.FC<GpsPointWithAddressProps> = ({
  point,
  cachedAddress,
  onAddressLoaded
}) => {
  const [showCoordinates, setShowCoordinates] = useState(false);
  const [address, setAddress] = useState<string | null>(cachedAddress || null);
  const { getAddressFromCoords, loading } = useGoogleGeocoding();

  useEffect(() => {
    if (!address && !loading) {
      const loadAddress = async () => {
        try {
          const result = await getAddressFromCoords(point.latitude, point.longitude);
          if (result) {
            setAddress(result);
            onAddressLoaded?.(result);
          }
        } catch (error) {
          console.error('Ошибка получения адреса:', error);
        }
      };

      // Добавляем небольшую задержку для предотвращения слишком частых запросов
      const timer = setTimeout(loadAddress, Math.random() * 1000);
      return () => clearTimeout(timer);
    }
  }, [point.latitude, point.longitude, address, loading, getAddressFromCoords, onAddressLoaded]);

  const handleCopyAddress = () => {
    if (address) {
      navigator.clipboard.writeText(address);
      toast.success('Адрес скопирован в буфер обмена');
    }
  };

  const handleCopyCoordinates = () => {
    const coords = `${point.latitude.toFixed(6)}, ${point.longitude.toFixed(6)}`;
    navigator.clipboard.writeText(coords);
    toast.success('Координаты скопированы в буфер обмена');
  };

  // Функция для сокращения длинных адресов
  const truncateAddress = (addr: string, maxLength: number = 60) => {
    if (addr.length <= maxLength) return addr;
    return addr.substring(0, maxLength) + '...';
  };

  return (
    <div className="flex items-start justify-between p-3 border rounded-lg gap-3">
      <div className="space-y-2 flex-1 min-w-0">
        <div className="flex items-start gap-2">
          <MapPin className="h-4 w-4 mt-0.5 text-primary flex-shrink-0" />
          <div className="flex-1 min-w-0">
            {loading ? (
              <Skeleton className="h-4 w-full" />
            ) : address ? (
              <div className="space-y-1">
                <div className="text-sm font-medium flex items-start gap-2">
                  <span className="break-words" title={address}>
                    {truncateAddress(address)}
                  </span>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-6 w-6 p-0 flex-shrink-0"
                    onClick={handleCopyAddress}
                    title="Копировать адрес"
                  >
                    <Copy className="h-3 w-3" />
                  </Button>
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-5 text-xs text-muted-foreground p-0"
                  onClick={() => setShowCoordinates(!showCoordinates)}
                >
                  {showCoordinates ? 'Скрыть координаты' : 'Координаты'}
                  {showCoordinates ? (
                    <ChevronUp className="h-3 w-3 ml-1" />
                  ) : (
                    <ChevronDown className="h-3 w-3 ml-1" />
                  )}
                </Button>
                {showCoordinates && (
                  <div className="text-xs text-muted-foreground font-mono flex items-center gap-2 break-all">
                    {point.latitude.toFixed(6)}, {point.longitude.toFixed(6)}
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-4 w-4 p-0 flex-shrink-0"
                      onClick={handleCopyCoordinates}
                      title="Копировать координаты"
                    >
                      <Copy className="h-2 w-2" />
                    </Button>
                  </div>
                )}
              </div>
            ) : (
              <div className="text-sm text-muted-foreground font-mono flex items-center gap-2 break-all">
                {point.latitude.toFixed(6)}, {point.longitude.toFixed(6)}
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-6 w-6 p-0 flex-shrink-0"
                  onClick={handleCopyCoordinates}
                  title="Копировать координаты"
                >
                  <Copy className="h-3 w-3" />
                </Button>
              </div>
            )}
          </div>
        </div>
        <div className="text-xs text-muted-foreground flex items-center gap-1">
          <Clock className="h-3 w-3" />
          {format(new Date(point.created_at), 'dd.MM.yyyy HH:mm:ss', { locale: ru })}
        </div>
      </div>
      <div className="text-right space-y-1 flex-shrink-0">
        <Badge variant="outline" className="flex items-center gap-1 text-xs">
          <MapPin className="h-3 w-3" />
          +{point.rental_mileage} км
        </Badge>
        {point.speed && (
          <div className="text-xs text-muted-foreground">
            {point.speed} км/ч
          </div>
        )}
      </div>
    </div>
  );
};
